/**
 * Hold the page still while something is open over it.
 *
 * Reported by the owner (2026-09-22): with a card open, the board behind it scrolled. Two things go
 * wrong when it does, and the second is the one that costs. The wheel lands on whatever is under the
 * pointer, so scrolling inside an opened card **falls through** to the board the moment the card's
 * own content ends — which reads as the card jumping. And closing it leaves the board somewhere
 * other than where it was left, so the card somebody wanted next is no longer under their cursor.
 *
 * **A counter, not a flag.** Confirmations open *from inside* the opened card — Remove is right
 * there in its action row — so two locks are live at once. With a boolean, dismissing the
 * confirmation would free the page while the card is still open: the bug returning in the one case
 * somebody is most likely to hit, mid-decision.
 *
 * The counter is `depth.ts`, apart from the DOM work, because that is the part with a rule in it and
 * this project tests rules in Node. What is left here is three lines of `style` assignment.
 */

import { useEffect } from 'react'

import { enter, leave } from './scrollDepth'

let restore: { overflow: string; paddingRight: string } | null = null

/**
 * The element that actually scrolls — `html` in standards mode, not `body`.
 *
 * This is measured, not assumed. The first version set `overflow: hidden` on `document.body`, which
 * is the recipe everyone writes down, and a render check showed the page still moving: the scroll
 * belongs to `documentElement` here, so the rule was applied to an element that was not scrolling.
 * `scrollingElement` asks the browser instead of guessing.
 */
function scroller(): HTMLElement {
  return (document.scrollingElement as HTMLElement | null) ?? document.body
}

function lock(): void {
  if (!enter()) return
  const body = scroller()
  restore = { overflow: body.style.overflow, paddingRight: body.style.paddingRight }
  //: Measured **before** `overflow: hidden` removes the bar — afterwards it is always 0. Without the
  //: compensation the whole page jumps a few pixels wider as a dialog opens and back as it closes,
  //: which looks like a rendering fault and is the kind of small wrongness people notice without
  //: being able to name it.
  const bar = window.innerWidth - document.documentElement.clientWidth
  body.style.overflow = 'hidden'
  if (bar > 0) {
    const current = parseFloat(getComputedStyle(body).paddingRight) || 0
    body.style.paddingRight = `${current + bar}px`
  }
}

function unlock(): void {
  if (!leave() || !restore) return
  //: Whatever the page had, not "nothing" — a route that sets its own `overflow` must get it back.
  const body = scroller()
  body.style.overflow = restore.overflow
  body.style.paddingRight = restore.paddingRight
  restore = null
}

/** Lock the page for as long as this component is mounted. */
export function useScrollLock(): void {
  useEffect(() => {
    lock()
    return unlock
  }, [])
}
